import styled from "styled-components";
import { message, Upload } from "antd";
import ImgCrop from "antd-img-crop";
import { AxiosError } from "axios";
import {
  AiFillCamera,
  AiFillEdit,
  AiFillHome,
  AiFillSetting,
  AiOutlineHome,
  AiOutlineLoading,
  AiOutlineSafety,
} from "react-icons/ai";
import {
  IoIosFilm,
  IoIosInformationCircleOutline,
  IoIosPeople,
} from "react-icons/io";
import {
  MdBusinessCenter,
  MdOutlineBusinessCenter,
} from "react-icons/md";

import { Card } from "../../ui/card/card.component";
import { colors } from "../../../theme/colors";
import { TextButton } from "../../ui/text-button/text-button.component";
import { Column } from "../../ui/grid/column.component";
import { Spacer } from "../../ui/spacer/spacer.component";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Child } from "../../../types/Children";
import { Bubble } from "../../ui/bubble/bubble.component";
import { authAtom } from "../../../atoms/auth.atom";
import { useAtom } from "jotai";
import Hamburger from "hamburger-react";
import { notificationsAtom } from "../../../atoms/notifications.atom";
import { axios } from "../../../api/axios";
import { Ticket } from "../../../models/ticket.model";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import { ResponseWithMessage } from "../../../types/ResponseWithMessage";
import { User } from "../../../models/user.model";

export const navLinks: {
  label: string;
  link: string;
  icon: (active: boolean) => Child;
  notifications?: number;
}[] = [
    {
      label: "Home",
      link: "/",
      icon: (active: boolean) =>
        active ? (
          <AiFillHome {...getNavIconProps(active)} />
        ) : (
          <AiOutlineHome {...getNavIconProps(active)} />
        ),
    },
    {
      label: "Films",
      link: "/f",
      icon: (active: boolean) => <IoIosFilm {...getNavIconProps(active)} />,
    },
    {
      label: "Business Profile",
      link: "/b",
      icon: (active: boolean) =>
        active ? (
          <MdBusinessCenter {...getNavIconProps(active)} />
        ) : (
          <MdOutlineBusinessCenter {...getNavIconProps(active)} />
        ),
    },
    {
      label: "Professional Profile",
      link: "/p",
      icon: (active: boolean) => <IoIosPeople {...getNavIconProps(active)} />,
    },
    {
      label: "Support Tickets",
      link: "/t",
      icon: (active: boolean) => (
        <IoIosInformationCircleOutline {...getNavIconProps(active)} />
      ),
    },
    {
      label: "Settings",
      link: "/settings",
      icon: (active: boolean) => <AiFillSetting {...getNavIconProps(active)} />,
    },
    {
      label: "Policies",
      link: "/policies",
      icon: (active: boolean) => <AiOutlineSafety {...getNavIconProps(active)} />,
    },
  ];

export const Sidebar = () => {
  const navigate = useNavigate();

  const [auth, setAuth] = useAtom(authAtom);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [notifications, setNotifications] = useAtom(notificationsAtom);

  const [activeLinkIndex, setActiveLinkIndex] = useState<number | null>(null);
  const [isShowingSidebar, setIsShowingSidebar] = useState(false);

  const location = useLocation();

  useEffect(() => {
    axios
      .get<Ticket[]>("/client/tickets")
      .then((resp) => {
        setNotifications((notifications) => [
          ...notifications.filter(
            (notification) => notification.name !== "/tickets"
          ),
          { name: "/tickets", number: resp.data.length },
        ]);
      })
      .catch(handleResponse);
  }, []);

  useEffect(() => {
    const index =
      location.pathname === "/"
        ? 0
        : navLinks.findIndex(
          (link) =>
            link.link !== "/" &&
            (location.pathname === link.link ||
              location.pathname.startsWith(`${link.link}/`))
        );

    if (index >= 0) {
      setActiveLinkIndex(index);
    }
  }, [location]);

  return (
    <Main>
      <HamburgerButton>
        <Hamburger
          toggled={isShowingSidebar}
          toggle={setIsShowingSidebar}
          size={18}
          rounded
          color={colors.text.hex}
        />
      </HamburgerButton>
      <ProfileCard $show={isShowingSidebar}>
        <ProfilePictureContainer>
          <ProfilePicture
            src={auth.user?.profile_photo ?? "/images/ui/avatar.png"}
            onClick={() => navigate("/profile")}
          />
          <ChangePhotoTrigger>
            <ImgCrop aspect={1} rotationSlider>
              <Upload
                accept="image/jpeg,image/png"
                showUploadList={false}
                beforeUpload={(file) => {
                  const isJpgOrPng =
                    ["image/jpeg", "image/png"].indexOf(file.type) > -1;
                  if (!isJpgOrPng) {
                    message.error("You can only upload JPG/PNG file!");
                    return Upload.LIST_IGNORE;
                  }
                  if (file.size / 1024 / 1024 >= 10) {
                    message.error("Image must be smaller than 10MB!");
                    return Upload.LIST_IGNORE;
                  }
                  return true;
                }}
                customRequest={async ({ file, onSuccess, onError }) => {
                  setIsUploadingPhoto(true);
                  try {
                    const formData = new FormData();
                    formData.append("profile_photo", file as File);
                    const result = await axios.put<
                      ResponseWithMessage<{ user: User }>
                    >("/auth/update/user/profile", formData);
                    handleResponse(result, (res) => {
                      setAuth({
                        authenticated: true,
                        loaded: true,
                        user: res.data.user,
                      });
                      message.success("Profile picture updated");
                    });
                    onSuccess?.("ok");
                  } catch (e) {
                    onError?.(e as Error);
                    if (e instanceof AxiosError) handleResponse(e);
                    else message.error("Could not update profile picture");
                  } finally {
                    setIsUploadingPhoto(false);
                  }
                }}
              >
                <ChangePhotoButton
                  type="button"
                  disabled={isUploadingPhoto}
                  aria-label="Change profile picture"
                  title="Change profile picture"
                >
                  {isUploadingPhoto ? (
                    <SpinningIcon>
                      <AiOutlineLoading size={14} color={colors.white.hex} />
                    </SpinningIcon>
                  ) : (
                    <AiFillCamera size={14} color={colors.white.hex} />
                  )}
                </ChangePhotoButton>
              </Upload>
            </ImgCrop>
          </ChangePhotoTrigger>
        </ProfilePictureContainer>
        <Column>
          <ProfileName>
            {auth.user?.full_name ??
              (auth.user?.first_name
                ? `${auth.user?.first_name} ${auth.user?.last_name ?? ""}`
                : "Pocketfilms User")}
          </ProfileName>
          <TextButton
            onClick={() => null}
            leadingIcon={<AiFillEdit size={15} color={colors.primary.hex} />}
          >
            <Link
              to="/profile"
              style={{ textDecoration: "none", color: colors.primary.hex }}
            >
              Edit Profile
            </Link>
          </TextButton>
        </Column>
      </ProfileCard>
      <NavCard $show={isShowingSidebar}>
        <Column style={{ width: "100%" }}>
          <Logo />
          <Spacer direction="bottom" size={20} />
          <Separator></Separator>
          <Spacer direction="bottom" size={20} />
          {navLinks.map((link, index) => {
            const active = index === activeLinkIndex;
            const nNotifications =
              notifications.find(({ name, number }) => name === link.link)
                ?.number ?? 0;

            return (
              <NavItem to={link.link} $active={active}>
                {nNotifications ? (
                  <Bubble
                    style={{ transform: "scale(0.8) transform(50%, -50%)" }}
                  >
                    {nNotifications}
                  </Bubble>
                ) : null}
                {link.icon(active)}
                {link.label}
              </NavItem>
            );
          })}
        </Column>
        <Spacer size={20} direction={"top"} />
        <div style={{ width: "100%" }}>
          <PlayButton href="https://play.pocketfilms.in">Play</PlayButton>
          <LogoutButton to="/logout">Logout</LogoutButton>
        </div>
      </NavCard>
    </Main>
  );
};

const Main = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 22vw;
  min-width: min(100%, 30rem);
  gap: 2rem;

  @media only screen and (max-width: 75em) {
    min-width: min(100%, 24rem);
  }

  @media only screen and (max-width: 48em) {
    position: absolute;
    top: 0;
    left: 0;
    padding: 2rem;
  }
`;

const HamburgerButton = styled.div`
  position: fixed;
  top: 0.5rem;
  left: 0.5rem;
  display: none;

  @media only screen and (max-width: 48em) {
    display: block;
  }
`;

const ProfileCard = styled(Card) <{
  $show?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 7.5rem;
  gap: 1rem;
  padding: 0 2rem 2rem 2rem;

  @media only screen and (max-width: 48em) {
    transform: translateX(${({ $show }) => ($show ? "0" : "-120%")});
    transition: all 0.7s ease-in-out;
    z-index: 4;
  }
`;

const ProfilePictureContainer = styled.div`
  position: relative;
  width: 15rem;
  height: 15rem;

  margin-top: -7.5rem;

  & > * {
    cursor: pointer;
  }
`;

const ProfilePicture = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 0.25rem solid ${colors.complement.hex};
  object-fit: contain;
  background-color: ${colors.background.hex};

  @media only screen and (max-width: 48em) {
    width: 10rem;
    height: 10rem;
  }
`;

const ChangePhotoTrigger = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 1;

  .ant-upload,
  .ant-upload-wrapper {
    display: block;
    line-height: 0;
  }
`;

const ChangePhotoButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3.2rem;
  height: 3.2rem;
  background-color: ${colors.primary.hex};
  border: 0.25rem solid ${colors.background.hex};
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  transition: transform 0.15s ease-out, background-color 0.15s ease-out;

  &:hover:not(:disabled) {
    transform: scale(1.08);
    background-color: ${colors.primary.withOpacity(0.85).hex};
  }

  &:disabled {
    cursor: progress;
    opacity: 0.85;
  }
`;

const SpinningIcon = styled.span`
  display: inline-flex;
  animation: spin 0.9s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ProfileName = styled.h3`
  font-size: 2.3rem;
  font-weight: 600;
  text-align: center;
`;

const NavCard = styled(Card) <{
  $show?: boolean;
}>`
  display: flex;
  flex-direction: column;
  padding: 3rem 2rem;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  width: 100%;

  @media only screen and (max-width: 48em) {
    transform: translateX(${({ $show }) => ($show ? "0" : "-120%")});
    transition: all 0.7s ease-in-out;
    z-index: 4;
  }
`;

const Logo = styled.img.attrs({
  src: "/images/brand/logo.png",
})`
  width: 10rem;
`;

const Separator = styled.div`
  width: 100%;
  height: 0.5px;
  background-color: ${colors.text.withOpacity(0.34).hex};
`;

const getNavIconProps = (active: boolean) => ({
  size: 15,
  color: active ? colors.white.hex : colors.text.hex,
});

const NavItem = styled(Link) <{
  $active?: boolean;
}>`
  position: relative;
  width: 100%;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 1rem;
  border-radius: 0.5rem;
  font-size: 1.35rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  background-color: ${({ $active }) =>
    $active ? colors.primary.hex : "transparent"};
  transition: background-color 0.5s ease-out;
  color: ${({ $active }) => ($active ? colors.white.hex : colors.text.hex)};
  margin-bottom: 0.5rem;
  text-decoration: none;

  /* Prevents accidental label selection when clicking */
  -webkit-user-select: none;
  user-select: none;

  &:hover {
    background-color: ${({ $active }) =>
    $active ? colors.primary.hex : colors.grey.hex};
  }
`;

const LogoutButton = styled(NavItem)`
  margin-top: 1rem;
  background-color: ${colors.complement.hex} !important;
  color: ${colors.black.hex};
  justify-content: center;
`;

const PlayButton = styled.a`
  width: 100%;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  font-size: 1.35rem;
  font-weight: 600;
  text-decoration: none;
  background-color: ${colors.primary.hex};
  color: ${colors.white.hex};
  transition: background-color 0.5s ease-out;

  &:hover {
    background-color: ${colors.primary.withOpacity(0.85).hex};
  }
`;
